import { Link } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { walletModalOpenAtom } from '@/atoms/wallet-modal';
import type { CrossChainRoute } from '@/lib/query-options';
import MingcuteLightningLine from '~icons/mingcute/lightning-line';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { TransferInfo } from './transfer-info';

interface TransferConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  error: string | null;
  isRouteInfoFetching: boolean;
  receiver: string;
  fromAddress: string;
  fromAmount: string;
  toAmount: string;
  route: CrossChainRoute;
  onConfirm: () => void;
}

export function TransferConfirmDialog({
  open,
  onOpenChange,
  isPending,
  error,
  isRouteInfoFetching,
  receiver,
  fromAddress,
  fromAmount,
  toAmount,
  route,
  onConfirm,
}: TransferConfirmDialogProps) {
  const { t } = useTranslation();
  const setConnectWalletModalIsOpen = useSetAtom(walletModalOpenAtom);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (isPending) return;
        onOpenChange(value);
      }}
    >
      <div className="flex flex-col space-y-2">
        <DialogTrigger asChild>
          <Button
            className="font-anta text-base mt-4"
            disabled={isPending || isRouteInfoFetching || !!error}
            variant={error ? 'secondary' : 'default'}
          >
            {isRouteInfoFetching ? (
              <Spinner className="size-5" />
            ) : error ? null : (
              <MingcuteLightningLine className="size-5" />
            )}
            {error ?? t('common.transfer')}
          </Button>
        </DialogTrigger>

        {!fromAddress && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setConnectWalletModalIsOpen(true);
              }}
            >
              {t('common.connectWallet')}
            </Button>
            <p className="text-foreground/50 text-xs text-center">
              {t('wallet.alreadyConnected')}{' '}
              <button
                className="text-blue-600 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.reload();
                }}
              >
                {t('wallet.tryRefresh')}
              </button>
            </p>
          </>
        )}

        <div className="text-center">
          <Link
            to="/history"
            className="text-xs text-foreground/60 underline hover:text-primary"
          >
            {t('common.history')}
          </Link>
        </div>
      </div>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('transfer.confirmTransfer')}</DialogTitle>
          <DialogDescription>
            {t('transfer.reviewTransferDetails')}
          </DialogDescription>
        </DialogHeader>

        <TransferInfo
          receiver={receiver}
          fromAddress={fromAddress}
          amount={fromAmount}
          recive={toAmount}
          route={route}
        />

        <DialogFooter>
          <DialogClose asChild disabled={isPending}>
            <Button disabled={isPending} variant="secondary">
              {t('common.cancel')}
            </Button>
          </DialogClose>
          <Button onClick={onConfirm} disabled={isPending}>
            {isPending ? (
              <Spinner className="size-4" />
            ) : (
              <>
                <MingcuteLightningLine className="size-4" />
                {t('common.confirm')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
